import { axiosUnauthorized } from "../utils/axios";
import config from "../config/config";

// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// City Types
export interface Zone {
  id: string;
  value: string;
  description: string;
  default_city_id: string;
  createdAt: string;
  updatedAt: string;
  Clusters: Cluster[];
}

export interface Cluster {
  id: string;
  value: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export interface City {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  state: string;
  region: string;
  is_default_for_location: boolean;
  description: string;
  is_active: boolean;
  createdAt: string;
  updatedAt: string;
  Zones: Zone[];
}

// Pagination interface for cities response
export interface PaginationInfo {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

// Cities search response interface
export interface CitiesResponse {
  success: boolean;
  data: City[];
  pagination: PaginationInfo;
}

// Cities search parameters
export interface CitiesSearchParams {
  searchTerm?: string;
  limit?: number;
  offset?: number;
}

// Price Types
export interface Price {
  id: string;
  vehicle_manufacturer: string;
  vehicle_model: string;
  fuel_type: string;
  fixed_fare: number;
  extra_fare: number;
  night_base_fare: number;
  free_waiting_minutes: number;
  is_admin: boolean;
  created_at: string;
  updated_at: string;
  Plan: {
    id: string;
    source: string;
    destination: string;
    distance: number;
    is_toll: boolean;
    toll_charges: number;
    is_driver: boolean;
    driver_charges: number;
    is_night: boolean;
    night_charges: number;
    is_waiting: boolean;
    waiting_charges: number;
    is_parking: boolean;
    parking_charges: number;
    is_tax: boolean;
    state_tax: number;
    green_tax: number;
  };
}

// Search Request Types
export interface SearchRequest {
  trip_type: string;
  source: string;
  destination: string;
  hours?: string; // For hourly bookings
}

class SearchService {
  private readonly baseUrl = config.ENDPOINTS.PUBLIC;

  // Search for prices
  async searchPrices(searchData: SearchRequest): Promise<ApiResponse<Price[]>> {
    try {
      const response = await axiosUnauthorized.post<ApiResponse<Price[]>>(
        `${this.baseUrl}/search`,
        searchData
      );
      return response.data;
    } catch (error: any) {
      console.error("Search prices error:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Failed to search prices",
      };
    }
  }

  // Get price by ID
  async getPriceById(id: string): Promise<ApiResponse<Price>> {
    try {
      const response = await axiosUnauthorized.get<ApiResponse<Price>>(
        `${this.baseUrl}/get-price/${id}`
      );
      return response.data;
    } catch (error: any) {
      console.error("Get price by ID error:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Failed to get price details",
      };
    }
  }

  // Get cities with search functionality
  async getCities(params?: CitiesSearchParams): Promise<CitiesResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params?.searchTerm) {
        queryParams.append("searchTerm", params.searchTerm);
      }
      if (params?.limit) {
        queryParams.append("limit", params.limit.toString());
      }
      if (params?.offset) {
        queryParams.append("offset", params.offset.toString());
      }

      const url = `${this.baseUrl}/get-cities${
        queryParams.toString() ? "?" + queryParams.toString() : ""
      }`;
      const response = await axiosUnauthorized.get<CitiesResponse>(url);
      return response.data;
    } catch (error: any) {
      console.error("Get cities error:", error);
      return {
        success: false,
        data: [],
        pagination: {
          total: 0,
          limit: 50,
          offset: 0,
          hasMore: false,
        },
      };
    }
  }

  // Legacy method for backward compatibility
  async getAllCities(): Promise<ApiResponse<City[]>> {
    try {
      const response = await this.getCities({ limit: 1000 });
      return {
        success: response.success,
        data: response.data,
        error: response.success ? undefined : "Failed to get cities",
      };
    } catch (error: any) {
      console.error("Get all cities error:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Failed to get cities",
      };
    }
  }

  // Search cities using the API with debouncing support
  async searchCities(searchTerm: string, limit: number = 8): Promise<City[]> {
    try {
      // Even without a search term, get popular cities (with a higher limit if no term)
      const response = await this.getCities({
        searchTerm: searchTerm || undefined,
        limit: searchTerm ? limit : 10,
      });
      console.log("Search cities API response:", response);

      if (response.success && response.data) {
        return response.data;
      }

      return [];
    } catch (error) {
      console.error("Search cities error:", error);
      return [];
    }
  }

  // Helper method for local filtering (fallback)
  searchCitiesLocally(cities: City[], searchTerm: string): City[] {
    if (!searchTerm || searchTerm.length < 2) {
      return cities.slice(0, 10);
    }

    const normalizedSearch = searchTerm.toLowerCase();
    return cities
      .filter(
        (city) =>
          city.name.toLowerCase().includes(normalizedSearch) ||
          city.state.toLowerCase().includes(normalizedSearch) ||
          city.region.toLowerCase().includes(normalizedSearch) ||
          city.description?.toLowerCase().includes(normalizedSearch)
      )
      .slice(0, 8);
  }
}

export const searchService = new SearchService();
export default searchService;
